import { environment } from '@environments/environment';

/**
 * Removes the cached OpenID Connect discovery document from storage when its
 * issuer no longer matches the configured authority.
 *
 * angular-auth-oidc-client persists the well-known endpoints (including the
 * issuer) under a storage key that matches the configId. When the authority
 * changes but the configId stays the same, the library loads the stale cached
 * document and throws an "Issuer mismatch" error. Stripping only the cached
 * well-known endpoints forces a fresh discovery fetch while keeping any
 * still-valid tokens intact.
 *
 * The library uses sessionStorage by default, but a custom configuration may
 * use localStorage, so both are inspected.
 */
export function cleanStaleAuthWellKnownCache(): void {
  const { configId, authority } = environment.authConfig;
  if (!configId || !authority) {
    return;
  }

  cleanStorage(sessionStorage, configId, authority);
  cleanStorage(localStorage, configId, authority);
}

function cleanStorage(storage: Storage, configId: string, authority: string): void {
  const raw = storage.getItem(configId);
  if (!raw) {
    return;
  }

  try {
    const stored = JSON.parse(raw) as Record<string, unknown>;
    const inner = (stored[configId] ?? stored) as Record<string, unknown>;
    const wellKnown = inner['authWellKnownEndPoints'] as { issuer?: string } | undefined;
    const cachedIssuer = wellKnown?.issuer;
    if (!cachedIssuer) {
      return;
    }

    if (!isSameOrigin(cachedIssuer, authority)) {
      delete inner['authWellKnownEndPoints'];
      storage.setItem(configId, JSON.stringify(stored));
    }
  } catch {
    // Corrupt entry: clear it so the library can rebuild from scratch.
    storage.removeItem(configId);
  }
}

function isSameOrigin(a: string, b: string): boolean {
  try {
    return new URL(a).origin === new URL(b).origin;
  } catch {
    return false;
  }
}
